import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { toast } from 'sonner';
import { Copy, Ticket, Check } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const fmtValue = (c) => {
  if (c.type === 'percentage') return `${Number(c.value || 0)}% OFF`;
  if (c.type === 'flat') return `\u20B9${Number(c.value || 0)} OFF`;
  return `${c.value} OFF`;
};

const fmtExpiry = (iso) => {
  if (!iso) return 'No expiry';
  try {
    const d = new Date(iso);
    return `Valid till ${d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}`;
  } catch { return `Valid till ${iso}`; }
};

const CouponsDialog = ({ open, onOpenChange }) => {
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(false);
  const [copiedCode, setCopiedCode] = useState('');

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    axios
      .get(`${API}/api/coupons/public`)
      .then((res) => setCoupons(res.data || []))
      .catch(() => setCoupons([]))
      .finally(() => setLoading(false));
  }, [open]);

  const copy = async (code) => {
    try {
      await navigator.clipboard.writeText(code);
      setCopiedCode(code);
      toast.success(`Coupon "${code}" copied. Paste it at checkout.`);
      setTimeout(() => setCopiedCode(''), 1600);
    } catch {
      toast.error('Copy failed. Long-press the code to copy manually.');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg bg-white border border-[#E0E0E0] rounded-2xl" data-testid="coupons-dialog">
        <DialogHeader className="text-left">
          <div className="flex items-center gap-2 mb-1">
            <div className="p-2 rounded-full bg-primary/10">
              <Ticket className="h-4 w-4 text-primary" />
            </div>
            <DialogTitle className="font-heading text-2xl font-black tracking-tight">Available Coupons</DialogTitle>
          </div>
          <DialogDescription className="text-sm text-muted-foreground">
            Tap a code to copy it. Paste it at checkout to redeem your discount.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 max-h-[60vh] overflow-y-auto pt-2" data-testid="coupons-list">
          {loading && (
            <div className="py-12 flex items-center justify-center" data-testid="coupons-loading">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
            </div>
          )}
          {!loading && coupons.length === 0 && (
            <div className="py-10 text-center text-sm text-muted-foreground" data-testid="no-active-coupons">
              No active coupons at the moment. Check back soon!
            </div>
          )}
          {!loading && coupons.map((c) => {
            const isCopied = copiedCode === c.code;
            return (
              <div
                key={c.code}
                className="relative rounded-xl border border-dashed border-primary/40 bg-gradient-to-br from-primary/5 to-white p-4 flex items-center gap-3"
                data-testid={`coupon-row-${c.code}`}
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-baseline gap-2 mb-1">
                    <span className="font-mono font-bold text-primary text-lg tracking-wider truncate" data-testid={`coupon-code-${c.code}`}>{c.code}</span>
                    <span className="text-[10px] font-bold uppercase text-emerald-700 bg-emerald-50 border border-emerald-200 px-1.5 py-0.5 rounded">{fmtValue(c)}</span>
                  </div>
                  <p className="text-[11px] text-muted-foreground leading-tight">
                    {c.min_order_amount ? <>Min order &#8377;{Number(c.min_order_amount)}. </> : null}
                    {c.max_discount ? <>Max discount &#8377;{Number(c.max_discount)}. </> : null}
                    {fmtExpiry(c.expiry_date)}
                  </p>
                  {c.description && <p className="text-[11px] text-foreground/70 mt-1 italic">{c.description}</p>}
                </div>
                <Button
                  size="sm"
                  variant={isCopied ? 'secondary' : 'outline'}
                  className="rounded-full h-8 shrink-0"
                  onClick={() => copy(c.code)}
                  data-testid={`copy-coupon-${c.code}`}
                >
                  {isCopied ? <><Check className="h-3.5 w-3.5 mr-1" /> Copied</> : <><Copy className="h-3.5 w-3.5 mr-1" /> Copy</>}
                </Button>
              </div>
            );
          })}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CouponsDialog;
